import Decimal from 'decimal.js';
import { compact, difference, isEmpty } from 'lodash';

import { BOTH_GRANTORS_SENTINEL } from '@/hooks/useDualGrantorOptions';
import { InsurancePolicyDetails } from '@/modules/entities/InsurancePolicyDetailsSubform/InsurancePolicyDetailsSubform.types';
import {
  AugmentedCreateIlitTrustInput,
  AugmentedCreateInsurancePolicyInput,
  AugmentedUpdateInsurancePolicyInput,
  CreateInsurancePolicyInput,
  InsurancePolicyKind,
  InsurancePolicyPremiumPaymentSourceKind,
  InsurancePolicyPremiumPaymentTransferTaxKind,
  UpdateIlitTrustInput,
  UpdateInsurancePolicyInput,
} from '@/types/schema';
import { ValidationError } from '@/utils/errors';
import { getPulidKind, PulidKind } from '@/utils/pulid';

import { SubformsCombinedType } from '../../EntitySubforms.types';

function assertHasValidPolicyHolderList(policy: InsurancePolicyDetails): void {
  if (
    policy.policyType === InsurancePolicyKind.Term &&
    compact(policy.policyHolderIDs).length !== 1
  ) {
    throw new ValidationError({
      message:
        'Select exactly one insured person for each term life policy in Insurance policy details before saving.',
    });
  }

  if (
    policy.policyType !== InsurancePolicyKind.Term &&
    policy.isConvertible &&
    policy.policyHolderIDs.length < 2
  ) {
    throw new Error('Survivorship policies require two insured individuals.');
  }
  return;
}

function getTermDuration(
  policyType: InsurancePolicyKind,
  termLength: number | string
) {
  if (policyType !== InsurancePolicyKind.Term) {
    return undefined;
  }

  // Validate the payload even when the policy's form fields are not mounted.
  if (typeof termLength === 'string') {
    throw new ValidationError({
      message:
        'Select a term length for each term life policy in Insurance policy details before saving.',
    });
  }

  return termLength;
}

function getCreatePremiumPaymentSource(
  policy: Pick<
    InsurancePolicyDetails,
    | 'premiumPaymentSourceID'
    | 'premiumPaymentTransferTaxKind'
    | 'premiumPaymentGiftingGrantorIDs'
  >
): Pick<
  CreateInsurancePolicyInput,
  | 'premiumPaymentSourceEntityID'
  | 'premiumPaymentSourceIndividualID'
  | 'premiumPaymentSourceTestamentaryEntityID'
  | 'premiumPaymentSourceKind'
  | 'premiumPaymentTransferTaxKind'
  | 'premiumPaymentGiftingGrantorIDs'
> {
  if (!policy.premiumPaymentSourceID) {
    return {};
  }
  const pulid = getPulidKind(policy.premiumPaymentSourceID);

  const grantorData: Partial<CreateInsurancePolicyInput> = {};

  if (
    policy.premiumPaymentTransferTaxKind ===
    InsurancePolicyPremiumPaymentTransferTaxKind.AnnualExclusionGiftWithCrummeyPowers
  ) {
    grantorData.premiumPaymentTransferTaxKind =
      InsurancePolicyPremiumPaymentTransferTaxKind.AnnualExclusionGiftWithCrummeyPowers;
  } else if (policy.premiumPaymentTransferTaxKind === BOTH_GRANTORS_SENTINEL) {
    grantorData.premiumPaymentTransferTaxKind =
      InsurancePolicyPremiumPaymentTransferTaxKind.GrantorTaxableGift;
    grantorData.premiumPaymentGiftingGrantorIDs =
      policy.premiumPaymentGiftingGrantorIDs;
  } else if (
    policy.premiumPaymentTransferTaxKind ===
    InsurancePolicyPremiumPaymentTransferTaxKind.NonTaxable
  ) {
    grantorData.premiumPaymentTransferTaxKind =
      InsurancePolicyPremiumPaymentTransferTaxKind.NonTaxable;
  }

  if (
    policy.premiumPaymentSourceID ===
    InsurancePolicyPremiumPaymentSourceKind.Self
  ) {
    return {
      ...grantorData,
      premiumPaymentSourceKind: InsurancePolicyPremiumPaymentSourceKind.Self,
    };
  }

  switch (pulid) {
    case PulidKind.Entity:
      return {
        ...grantorData,
        premiumPaymentSourceKind:
          InsurancePolicyPremiumPaymentSourceKind.Entity,
        premiumPaymentSourceEntityID: policy.premiumPaymentSourceID,
      };
    case PulidKind.ClientProfile:
      return {
        ...grantorData,
        premiumPaymentSourceKind:
          InsurancePolicyPremiumPaymentSourceKind.Individual,
        premiumPaymentSourceIndividualID: policy.premiumPaymentSourceID,
      };
    case PulidKind.TestamentaryEntity:
      return {
        ...grantorData,
        premiumPaymentSourceKind:
          InsurancePolicyPremiumPaymentSourceKind.TestamentaryEntity,
        premiumPaymentSourceTestamentaryEntityID: policy.premiumPaymentSourceID,
      };
    default: {
      return {};
    }
  }
}

function mapPolicyToCreate(
  policy: InsurancePolicyDetails,
  householdId: string
): CreateInsurancePolicyInput {
  if (!policy.policyType) {
    throw new Error('No policy type selected');
  }

  assertHasValidPolicyHolderList(policy);

  return {
    ...getCreatePremiumPaymentSource(policy),
    carrierName: policy.insuranceCarrier,
    conversionDate: policy.isConvertible ? policy.conversionDate : undefined,
    deathBenefitAmount: policy.deathBenefitAmount || new Decimal(0),
    documentIDs: policy.documentIds,
    initialPremiumDueDate: policy.premiumInitialDueDate || undefined,
    kind: policy.policyType,
    notes: policy.notes,
    policyHolderIDs: compact(policy.policyHolderIDs),
    policyNumber: policy.policyNumber,
    integrationInsuranceID: policy.integrationInsuranceId || undefined,
    premiumAmount: policy.premiumAmount || undefined,
    premiumFrequency: policy.premiumFrequency || undefined,
    startDate: policy.policyStartDate || new Date(),
    survivorship: policy.isConvertible,
    termDurationYears: getTermDuration(policy.policyType, policy.termLength),
    cashValue: !policy.cashValue?.isZero() ? policy.cashValue : undefined,
    loanBalanceOutstanding: !policy.loanBalanceOutstanding?.isZero()
      ? policy.loanBalanceOutstanding
      : undefined,
    householdID: householdId,
  };
}

function getUpdatePremiumPaymentSource(
  policy: Pick<
    InsurancePolicyDetails,
    | 'premiumPaymentSourceID'
    | 'premiumPaymentTransferTaxKind'
    | 'premiumPaymentGiftingGrantorIDs'
    | 'initialPremiumPaymentGiftingGrantorIDs'
  >
): Pick<
  UpdateInsurancePolicyInput,
  | 'clearPremiumPaymentSourceEntity'
  | 'clearPremiumPaymentSourceIndividual'
  | 'clearPremiumPaymentSourceKind'
  | 'clearPremiumPaymentSourceTestamentaryEntity'
  | 'clearPremiumPaymentGiftingGrantors'
  | 'clearPremiumPaymentTransferTaxKind'
  | 'premiumPaymentSourceEntityID'
  | 'premiumPaymentSourceIndividualID'
  | 'premiumPaymentSourceTestamentaryEntityID'
  | 'premiumPaymentSourceKind'
  | 'addPremiumPaymentGiftingGrantorIDs'
  | 'removePremiumPaymentGiftingGrantorIDs'
  | 'premiumPaymentTransferTaxKind'
> {
  if (!policy.premiumPaymentSourceID) {
    return {
      clearPremiumPaymentSourceEntity: true,
      clearPremiumPaymentSourceIndividual: true,
      clearPremiumPaymentSourceTestamentaryEntity: true,
      clearPremiumPaymentSourceKind: true,
      clearPremiumPaymentGiftingGrantors: true,
      clearPremiumPaymentTransferTaxKind: true,
    };
  }
  const pulid = getPulidKind(policy.premiumPaymentSourceID);

  const grantorData: Partial<UpdateInsurancePolicyInput> = {};

  if (!policy.premiumPaymentTransferTaxKind) {
    grantorData.clearPremiumPaymentGiftingGrantors = true;
    grantorData.clearPremiumPaymentTransferTaxKind = true;
  } else if (
    policy.premiumPaymentTransferTaxKind ===
    InsurancePolicyPremiumPaymentTransferTaxKind.AnnualExclusionGiftWithCrummeyPowers
  ) {
    grantorData.premiumPaymentTransferTaxKind =
      InsurancePolicyPremiumPaymentTransferTaxKind.AnnualExclusionGiftWithCrummeyPowers;
    grantorData.clearPremiumPaymentGiftingGrantors = true;
  } else if (policy.premiumPaymentTransferTaxKind === BOTH_GRANTORS_SENTINEL) {
    const {
      initialPremiumPaymentGiftingGrantorIDs,
      premiumPaymentGiftingGrantorIDs,
    } = policy;
    grantorData.premiumPaymentTransferTaxKind =
      InsurancePolicyPremiumPaymentTransferTaxKind.GrantorTaxableGift;
    const addPremiumPaymentGiftingGrantorIDs = difference(
      premiumPaymentGiftingGrantorIDs,
      initialPremiumPaymentGiftingGrantorIDs
    );
    const removePremiumPaymentGiftingGrantorIDs = difference(
      initialPremiumPaymentGiftingGrantorIDs,
      premiumPaymentGiftingGrantorIDs
    );

    grantorData.addPremiumPaymentGiftingGrantorIDs = !isEmpty(
      addPremiumPaymentGiftingGrantorIDs
    )
      ? addPremiumPaymentGiftingGrantorIDs
      : undefined;
    grantorData.removePremiumPaymentGiftingGrantorIDs = !isEmpty(
      removePremiumPaymentGiftingGrantorIDs
    )
      ? removePremiumPaymentGiftingGrantorIDs
      : undefined;
  } else if (
    policy.premiumPaymentTransferTaxKind ===
    InsurancePolicyPremiumPaymentTransferTaxKind.NonTaxable
  ) {
    grantorData.premiumPaymentTransferTaxKind =
      InsurancePolicyPremiumPaymentTransferTaxKind.NonTaxable;
    grantorData.clearPremiumPaymentGiftingGrantors = true;
  }

  if (
    policy.premiumPaymentSourceID ===
    InsurancePolicyPremiumPaymentSourceKind.Self
  ) {
    return {
      clearPremiumPaymentSourceEntity: true,
      clearPremiumPaymentSourceIndividual: true,
      clearPremiumPaymentSourceTestamentaryEntity: true,
      ...grantorData,
      premiumPaymentSourceKind: InsurancePolicyPremiumPaymentSourceKind.Self,
    };
  }

  switch (pulid) {
    case PulidKind.Entity:
      return {
        ...grantorData,
        clearPremiumPaymentSourceIndividual: true,
        clearPremiumPaymentSourceTestamentaryEntity: true,
        premiumPaymentSourceKind:
          InsurancePolicyPremiumPaymentSourceKind.Entity,
        premiumPaymentSourceEntityID: policy.premiumPaymentSourceID,
      };
    case PulidKind.ClientProfile:
      return {
        ...grantorData,
        clearPremiumPaymentSourceEntity: true,
        clearPremiumPaymentSourceTestamentaryEntity: true,
        premiumPaymentSourceKind:
          InsurancePolicyPremiumPaymentSourceKind.Individual,
        premiumPaymentSourceIndividualID: policy.premiumPaymentSourceID,
      };
    case PulidKind.TestamentaryEntity:
      return {
        ...grantorData,
        clearPremiumPaymentSourceEntity: true,
        clearPremiumPaymentSourceIndividual: true,
        premiumPaymentSourceKind:
          InsurancePolicyPremiumPaymentSourceKind.TestamentaryEntity,
        premiumPaymentSourceTestamentaryEntityID: policy.premiumPaymentSourceID,
      };
    default: {
      return {};
    }
  }
}

function mapPolicyToUpdate(
  policy: InsurancePolicyDetails
): UpdateInsurancePolicyInput {
  if (!policy.policyId) {
    throw new Error('Attempted to update a policy without an ID');
  }

  if (!policy.policyType) {
    throw new Error('No policy type selected');
  }

  assertHasValidPolicyHolderList(policy);

  const output: Partial<UpdateInsurancePolicyInput> = {
    ...getUpdatePremiumPaymentSource(policy),
    deathBenefitAmount: policy.deathBenefitAmount,
    kind: policy.policyType,
    startDate: policy.policyStartDate,
    survivorship: policy.isConvertible,
    integrationInsuranceID: policy.integrationInsuranceId || undefined,
    clearIntegrationInsurance: !policy.integrationInsuranceId,
  };

  if (policy.premiumFrequency) {
    output.premiumFrequency = policy.premiumFrequency;
  } else {
    output.clearPremiumFrequency = true;
  }

  if (policy.premiumInitialDueDate) {
    output.initialPremiumDueDate = policy.premiumInitialDueDate;
  } else {
    output.clearInitialPremiumDueDate = true;
  }

  if (policy.premiumAmount && !policy.premiumAmount?.isZero()) {
    output.premiumAmount = policy.premiumAmount;
  } else {
    output.clearPremiumAmount = true;
  }

  if (policy.isConvertible) {
    output.conversionDate = policy.conversionDate;
  } else {
    output.clearConversionDate = true;
  }

  const nextTermLength = getTermDuration(policy.policyType, policy.termLength);
  if (nextTermLength) {
    output.termDurationYears = nextTermLength;
  } else {
    output.clearTermDurationYears = true;
  }

  if (policy.notes.length) {
    output.notes = policy.notes;
  } else {
    output.clearNotes = true;
  }

  if (policy.policyNumber) {
    output.policyNumber = policy.policyNumber;
  } else {
    output.clearPolicyNumber = true;
  }

  if (policy.insuranceCarrier.length) {
    output.carrierName = policy.insuranceCarrier;
  } else {
    output.clearCarrierName = true;
  }

  if (policy.cashValue && !policy.cashValue?.isZero()) {
    output.cashValue = policy.cashValue;
  } else {
    output.clearCashValue = true;
  }

  if (policy.cashValueDate) {
    output.cashValueDate = policy.cashValueDate;
  } else {
    output.clearCashValueDate = true;
  }

  if (!policy.loanBalanceOutstanding?.isZero()) {
    output.loanBalanceOutstanding = policy.loanBalanceOutstanding;
  } else {
    output.clearLoanBalanceOutstanding = true;
  }

  if (policy.documentIds.length === 0) {
    output.clearDocuments = true;
  } else {
    const { documentIds, initialDocumentIDs } = policy;

    const addDocumentIDs = difference(documentIds, initialDocumentIDs);
    const removeDocumentIDs = difference(initialDocumentIDs, documentIds);

    output.addDocumentIDs = addDocumentIDs.length ? addDocumentIDs : undefined;
    output.removeDocumentIDs = removeDocumentIDs.length
      ? removeDocumentIDs
      : undefined;
  }

  const {
    policyHolderIDs: uncompactPolicyHolderIDs,
    initialPolicyHolderIDs: uncompactInitialPolicyHolderIDs,
  } = policy;
  const policyHolderIDs = compact(uncompactPolicyHolderIDs);
  const initialPolicyHolderIDs = compact(uncompactInitialPolicyHolderIDs);

  if (policyHolderIDs.length === 0) {
    output.clearPolicyHolders = true;
  } else {
    const addPolicyHolderIDs = difference(
      policyHolderIDs,
      initialPolicyHolderIDs
    );
    const removePolicyHolderIDs = difference(
      initialPolicyHolderIDs,
      policyHolderIDs
    );

    output.addPolicyHolderIDs = addPolicyHolderIDs.length
      ? addPolicyHolderIDs
      : undefined;

    output.removePolicyHolderIDs = removePolicyHolderIDs.length
      ? removePolicyHolderIDs
      : undefined;
  }

  return output;
}

function isValidPolicy(
  policy: InsurancePolicyDetails | undefined | null
): boolean {
  // death benefit amount will be null if the policy is clean
  return policy?.deathBenefitAmount !== null;
}

export function makeCreatePolicyInputs(
  input: SubformsCombinedType['insurancePolicyDetailsSubform'],
  householdId: string
): Partial<AugmentedCreateIlitTrustInput> | undefined {
  const output: CreateInsurancePolicyInput[] = (input?.policies || [])
    .filter(isValidPolicy)
    .map((policy) => mapPolicyToCreate(policy, householdId));

  if (output.length) {
    return {
      withPolicies: output.map((policy) => ({
        create: policy,
      })),
    };
  }
  return;
}

export interface UpdateOutput {
  updatePolicies: AugmentedUpdateInsurancePolicyInput[];
  withPolicies: AugmentedCreateInsurancePolicyInput[];
  removePolicyIDs: UpdateIlitTrustInput['removePolicyIDs'];
}
export function makeUpdatePolicyInputs(
  input: SubformsCombinedType['insurancePolicyDetailsSubform'] | undefined,
  householdId: string
): UpdateOutput {
  if (!input) {
    throw new Error('No insurance policies details subform data available');
  }
  const { initialPolicyIDs = [], policies = [] } = input || {};
  const removePolicyIDs = initialPolicyIDs.filter((initialPolicyID) => {
    return !policies.find(({ policyId }) => policyId === initialPolicyID);
  });
  return policies.filter(isValidPolicy).reduce<UpdateOutput>(
    (acc, policy) => {
      if (policy.policyId) {
        acc.updatePolicies.push({
          id: policy.policyId,
          update: mapPolicyToUpdate(policy),
        });
      } else {
        acc.withPolicies.push({
          create: mapPolicyToCreate(policy, householdId),
        });
      }
      return acc;
    },
    { updatePolicies: [], withPolicies: [], removePolicyIDs }
  );
}
